import type { AdminAgentMessage as AdminAgentMessageEntry } from "../models/admin-agent.models";
import { formatAdminAgentTime } from "../utils/admin-agent.utils";
import "./admin-agent-markdown.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Keep tool results inspectable without letting long JSON responses dominate the conversation.
// Markdown uses ReactMarkdown's safe defaults and does not render raw HTML.
const AdminAgentMessage = ({ message }: { message: AdminAgentMessageEntry }) => {
  const label = message.role === "assistant" ? "Assistant" : message.role === "system" ? "Run update" : message.actor;

  if (message.role === "tool" || message.toolName) {
    return (
      <details
        data-test={message.role === "tool" ? "admin-agent-tool-message" : "admin-agent-tool-request"}
        data-message-id={message.id}
        className="rounded-lg border border-gray-200 bg-gray-50 text-xs dark:border-gray-700 dark:bg-gray-800/60"
      >
        <summary className="cursor-pointer break-words px-2 py-1 font-medium">
          {message.role === "tool" ? "Tool result" : "Tool request"} · {message.toolName ?? "Tool"}
        </summary>
        {message.toolCategory && (
          <p
            data-test="admin-agent-tool-category"
            className="px-3 pb-1 font-semibold text-amber-700 dark:text-amber-300"
          >
            {message.toolCategory} · {message.toolSubcategory}
          </p>
        )}
        <p className="px-3 pb-2 text-gray-500 dark:text-gray-400">
          {message.actor} · {formatAdminAgentTime(message.createdAt)}
        </p>
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words border-t border-gray-200 px-3 py-2 dark:border-gray-700">
          {message.content}
        </pre>
      </details>
    );
  }

  return (
    <article
      data-test={`admin-agent-${message.role}-message`}
      data-message-id={message.id}
      className={`rounded-lg px-3 py-2 text-sm ${message.role === "user" ? "ml-4 bg-gray-100 dark:bg-gray-800" : message.role === "system" ? "border-l-2 border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" : "bg-emerald-50/70 dark:bg-emerald-950/20"}`}
    >
      <div className="mb-1 flex items-baseline justify-between gap-2 text-[10px] text-gray-500 dark:text-gray-400">
        <span className="min-w-0 break-all font-semibold">{label || "Admin"}</span>
        <time dateTime={message.createdAt} className="shrink-0">
          {formatAdminAgentTime(message.createdAt)}
        </time>
      </div>
      {message.role === "assistant" ? (
        <div className="admin-agent-markdown min-w-0 break-words leading-5">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: ({ node, ...props }) => (
                <div className="overflow-x-auto" role="region" aria-label="Message table" tabIndex={0}>
                  <table {...props} />
                </div>
              ),
            }}
          >
            {message.content}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="whitespace-pre-wrap break-words leading-5">{message.content}</p>
      )}
    </article>
  );
};

export default AdminAgentMessage;
